"use client"

import { Link as LinkIcon, Loader2, AlertCircle } from "lucide-react"
import { useState, useEffect } from "react"
import { RevealOnScroll } from "@/components/reveal-on-scroll"

type LinkItem = {
    news_id: string
    news: string
    news_link: string
}

export function ParentInvolvementContent() {
    const [links, setLinks] = useState<LinkItem[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetchLinks = async () => {
            try {
                const response = await fetch('/api/links')
                const result = await response.json()

                if (result.status === 'success' && Array.isArray(result.data)) {
                    setLinks(result.data)
                } else {
                    throw new Error("Invalid data format")
                }
            } catch (err) {
                console.error("Error fetching links:", err)
                setError("Failed to load important links.")
            } finally {
                setLoading(false)
            }
        }

        fetchLinks()
    }, [])

    return (
        <section className="py-12 sm:py-16 lg:py-20 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <RevealOnScroll animationClass="animate-fade-in">
                    <div className="max-w-md mx-auto w-full">
                        <h2 className="text-3xl font-bold tracking-tight text-[#113545] sm:text-4xl mb-6 border-b-2 border-[#113545] inline-block pb-2">
                            PARENT-TEACHER ASSOCIATION
                        </h2>

                        <div className="bg-[#f0c352] p-4 mb-4 w-full">
                            <h3 className="text-xl font-bold text-black uppercase">PARENTS INVOLVEMENTS</h3>
                        </div>

                        <div className="mt-8 w-full">
                            <div className="bg-[#da9f10] text-black px-6 py-3 rounded-none inline-block mb-1 border-b-4 border-[#b8860b]">
                                <h3 className="font-semibold text-lg">Important Links</h3>
                            </div>
                            <div className="bg-[#113545] text-white p-6 h-[320px] overflow-hidden relative w-full border-b-2 border-[#da9f10]">
                                {loading ? (
                                    <div className="flex flex-col items-center justify-center h-full py-10 space-y-4">
                                        <Loader2 className="w-8 h-8 animate-spin text-[#da9f10]" />
                                        <p className="text-gray-300 text-sm italic font-medium">Fetching links...</p>
                                    </div>
                                ) : error ? (
                                    <div className="flex flex-col items-center justify-center h-full py-10 space-y-3 text-center">
                                        <AlertCircle className="w-8 h-8 text-red-400" />
                                        <p className="text-red-300 text-sm font-medium">{error}</p>
                                    </div>
                                ) : links.length > 0 ? (
                                    <div className="h-full overflow-hidden relative">
                                        <ul className="space-y-4 animate-vertical-scroll hover:pause-animation">
                                            {/* Original List */}
                                            {links.map((link) => (
                                                <li key={`${link.news_id}-orig`}>
                                                    <a
                                                        href={link.news_link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-3 bg-gray-200/90 p-4 hover:bg-white transition-all transform hover:scale-[1.02] group shadow-inner"
                                                    >
                                                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0 border-2 border-black group-hover:bg-[#da9f10] group-hover:border-[#da9f10] transition-colors">
                                                            <LinkIcon className="w-5 h-5 text-black group-hover:text-white rotate-45" />
                                                        </div>
                                                        <span className="font-bold text-black group-hover:text-[#113545] truncate uppercase tracking-tight text-sm">
                                                            {link.news}
                                                        </span>
                                                    </a>
                                                    <div className="h-0.5 w-1/3 bg-[#da9f10] mt-4 ml-4 opacity-30"></div>
                                                </li>
                                            ))}
                                            {/* Duplicated List for Seamless Loop */}
                                            {links.map((link) => (
                                                <li key={`${link.news_id}-dup`} aria-hidden="true">
                                                    <a
                                                        href={link.news_link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-3 bg-gray-200/90 p-4 hover:bg-white transition-all transform hover:scale-[1.02] group shadow-inner"
                                                    >
                                                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0 border-2 border-black group-hover:bg-[#da9f10] group-hover:border-[#da9f10] transition-colors">
                                                            <LinkIcon className="w-5 h-5 text-black group-hover:text-white rotate-45" />
                                                        </div>
                                                        <span className="font-bold text-black group-hover:text-[#113545] truncate uppercase tracking-tight text-sm">
                                                            {link.news}
                                                        </span>
                                                    </a>
                                                    <div className="h-0.5 w-1/3 bg-[#da9f10] mt-4 ml-4 opacity-30"></div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-full py-10">
                                        <p className="text-gray-400 text-sm italic">No links available at the moment.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </RevealOnScroll>
            </div>

            <style jsx>{`
                @keyframes vertical-scroll {
                    0% {
                        transform: translateY(0);
                    }
                    100% {
                        transform: translateY(-50%);
                    }
                }

                .animate-vertical-scroll {
                    animation: vertical-scroll 15s linear infinite;
                }

                .hover\\:pause-animation:hover {
                    animation-play-state: paused;
                }

                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: rgba(17, 53, 69, 0.5);
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #da9f10;
                    borderRadius: 10px;
                }
            `}</style>
        </section>
    )
}
